import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import Header from "../components/nayan/Header";
import Hero from "../components/nayan/Hero";
import HowItWorks from "../components/nayan/HowItWorks";
import ThreatMap from "../components/nayan/ThreatMap";
import Footer from "../components/nayan/Footer";

const REASONS = [
  {
    title: "Cut through cognitive warfare.",
    body: "We live in an era of weaponized information, AI-generated deepfakes, and hyper-partisan narratives. N.E.T.R.A cuts through the noise, detecting manipulation before it shapes your perspective.",
  },
  {
    title: "Beyond the binary 'True or False'.",
    body: "Misinformation isn't just about outright lies, it's about emotional manipulation, missing context, and deceptive framing. We deconstruct content across 8 independent axes to expose the full picture.",
  },
  {
    title: "Forensic intelligence, instantly.",
    body: "Manual fact-checking takes hours. N.E.T.R.A acts as your personal intelligence analyst, cross-referencing claims and verifying sources in seconds so you can react with confidence.",
  },
  {
    title: "Zero digital footprint.",
    body: "Your investigations are your own. We process your analysis locally in your session, ensuring your sensitive queries and uploaded documents are never indexed, published, or used to train models.",
  },
];

const TESTIMONIAL = {
  quote: "Finally a tool that respects how messy real-world content is. The 8-axis scoring is the right level of nuance, it doesn't pretend the world is binary.",
  author: ", a journalist friend, after seeing the prototype",
};

export default function LandingPage() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-ink-base text-white">
      <Header variant="marketing" />
      <Hero />

      {/* Why N.E.T.R.A - Mission & Philosophy */}
      <section id="why" className="border-t border-ink-border-subtle bg-gradient-to-b from-ink-base to-ink-surface/20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="text-sm text-brand font-medium tracking-wider uppercase">Mission</div>
            <h2 className="mt-3 text-4xl md:text-5xl font-semibold text-white tracking-tight leading-[1.05]">
              Built for the way{" "}
              <span className="font-serif italic font-light text-zinc-300">
                people actually read.
              </span>
            </h2>
            <p className="mt-5 text-zinc-400 text-lg leading-relaxed">
              Most fact-checkers either oversimplify ("True / False") or
              over-complicate (forty checkboxes nobody reads). N.E.T.R.A finds the
              middle: enough nuance to be useful, plain enough to be fast.
            </p>
          </div>

          {/* Testimonial - Now part of the mission section */}
          <div className="mt-16 max-w-3xl">
            <div className="rounded-2xl border border-ink-border-subtle bg-ink-surface/60 backdrop-blur p-9">
              <Quote size={22} className="text-brand mb-4" strokeWidth={1.5} />
              <p className="font-serif italic text-zinc-100 text-2xl leading-snug">
                {TESTIMONIAL.quote}
              </p>
              <p className="mt-5 text-sm text-zinc-500">{TESTIMONIAL.author}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features - Technical Capabilities */}
      <section id="features" className="border-t border-ink-border-subtle bg-ink-base">
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-24 md:py-32">
          <div className="mb-16">
            <div className="text-sm text-brand font-medium tracking-wider uppercase">Core Features</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-white tracking-tight">
              Intelligence you can <span className="font-serif italic font-light text-zinc-300">interrogate.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12">
            {REASONS.map((r, i) => (
              <div key={i} className="relative pl-8 group">
                <span className="absolute left-0 top-2 w-3 h-3 rounded-full bg-brand/40 group-hover:bg-brand transition-colors" />
                <h3 className="text-white text-xl font-medium tracking-tight">
                  {r.title}
                </h3>
                <p className="mt-2 text-zinc-400 leading-relaxed">{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HowItWorks />

      <ThreatMap />


      {/* Lab strip: Horizon Flare India identity */}
      <section className="border-t border-ink-border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-12 md:py-14">
          <a
            href="https://horizonflare.in"
            target="_blank"
            rel="noreferrer"
            data-testid="horizonflare-strip"
            className="group flex flex-col md:flex-row md:items-center md:justify-between gap-5 rounded-2xl border border-ink-border-subtle bg-gradient-to-br from-ink-surface/80 via-ink-surface/40 to-transparent backdrop-blur p-6 md:p-7 hover:border-signal-info/40 hover:shadow-[0_0_30px_rgba(143,184,224,0.2)] transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="relative w-11 h-11 rounded-xl border border-brand/30 bg-gradient-to-br from-brand/15 via-brand/5 to-transparent group-hover:border-signal-info/50 group-hover:bg-gradient-to-br group-hover:from-signal-info/20 group-hover:via-signal-info/10 group-hover:to-transparent flex items-center justify-center shrink-0 transition-all">
                <img 
                  src="/logo-icon.png" 
                  alt="Horizon Flare India" 
                  className="w-9 h-9 rounded-lg"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">An innovation lab project by</div>
                <div className="font-serif text-2xl text-white group-hover:text-brand transition-colors">
                  Horizon Flare India
                </div>
              </div>
            </div>
            <div className="flex items-center md:ml-auto -mt-2">
              <p className="text-sm text-zinc-400 max-w-md leading-relaxed">
                N.E.T.R.A emerged from our innovation lab in India - a groundbreaking initiative to restore clarity in the digital age. We're transforming how people interact with information, cutting through the noise to reveal truth, balance, and context in every story.
              </p>
              
            </div>
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-ink-border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-24 md:py-28 text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-[1.05] max-w-3xl mx-auto">
            Ready to see{" "}
            <span className="font-serif italic font-light text-brand">
              what others miss?
            </span>
          </h2>
          <p className="mt-5 text-zinc-400 max-w-xl mx-auto text-lg">
            Reveal the Truth. No sign-up, no setup. Bring any link, file, or
            paragraph - N.E.T.R.A will take it from there.
          </p>
          <button
            onClick={() => nav("/app")}
            data-testid="footer-cta"
            className="mt-9 group inline-flex items-center gap-2 bg-zinc-800/80 text-white hover:bg-zinc-700/80 px-7 py-3.5 rounded-full font-medium transition-all duration-300 shadow-md shadow-black/20 hover:shadow-lg hover:shadow-black/30 backdrop-blur-sm border border-zinc-700/50"
          >
            Reveal the Truth
            <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </button>
          <p className="mt-4 text-xs text-zinc-600">Free to try · no account needed · private to your session</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
